import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { ClientNotification } from "../../shared/models/clientNotification";
import { AnalyticsService } from "../../shared/services/analytics.service";
import { ClientNotificationService } from "../../shared/services/client-notification.service";
import { PlayerService } from "../../shared/services/player.service";
import { AuthRedirectService } from "../../shared/services/auth-redirect.service";
import { TranslateService } from "@ngx-translate/core";

type NotificationFilter = "all" | "match" | "manager" | "account";

@Component({
  selector: "app-notifications",
  templateUrl: "./notifications.component.html",
  styleUrls: ["../event.component.css", "./notifications.component.css"],
  standalone: false
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private static readonly OLD_NOTIFICATION_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;
  public readonly notifications$ = this.clientNotifications.notifications$;
  public readonly loading$ = this.clientNotifications.loading$;
  public readonly hasNotifications$ = this.notifications$.pipe(map((notifications) => (notifications || []).length > 0));
  public actionMessage: string;
  public actionError: string;
  public actionLoadingId: string;
  public confirmDismissAll = false;
  public activeFilter: NotificationFilter = "all";
  private subscriptions = new Subscription();

  constructor(
    private clientNotifications: ClientNotificationService,
    private playerService: PlayerService,
    private router: Router,
    private analytics: AnalyticsService,
    private authRedirect: AuthRedirectService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.notifications$.subscribe((notifications) => {
      const unreadIds = (notifications || [])
        .filter((notification) => !notification.read)
        .map((notification) => notification.id);
      if (unreadIds.length > 0) {
        this.clientNotifications.markRead(unreadIds);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleNotification(notification: ClientNotification): void {
    this.actionMessage = null;
    this.actionError = null;

    if (notification.type === "emailVerification") {
      this.sendVerificationEmail(notification);
    } else if (notification.type === "missingResult") {
      this.openMissingResultEvent(notification);
    } else if (notification.type === "ratingReview") {
      this.openRatingReview(notification);
    } else if (notification.type === "rulebookReminder") {
      this.openRulebook(notification);
    } else if (notification.type === "candidateRequest") {
      this.openPlayerManagement(notification);
    } else if (notification.type === "managerJoinRequest") {
      this.openPlayerManagement(notification);
    } else if (notification.type === "profileLocationMissing") {
      this.router.navigate(["/player-detail", false], {queryParams: {section: "location", returnTo: "notifications"}});
    } else if (notification.type === "noActiveMatches") {
      this.router.navigate(["/event-list"]);
    } else if (notification.event) {
      this.openActiveEvent(notification.event);
    } else {
      this.router.navigate(["/event-list"]);
    }
  }

  public dismissNotification(notification: ClientNotification): void {
    if (!notification) {
      return;
    }

    this.clientNotifications.dismiss(notification.id);
  }

  public requestDismissAll(): void {
    this.confirmDismissAll = true;
    this.actionMessage = null;
    this.actionError = null;
  }

  public cancelDismissAll(): void {
    this.confirmDismissAll = false;
  }

  public dismissAllNotifications(notifications: ClientNotification[]): void {
    const notificationIds = (notifications || []).map((notification) => notification.id);
    if (notificationIds.length === 0) {
      this.confirmDismissAll = false;
      return;
    }

    this.clientNotifications.dismissAll(notificationIds);
    this.confirmDismissAll = false;
  }

  public setFilter(filter: NotificationFilter): void {
    this.activeFilter = filter;
    this.confirmDismissAll = false;
  }

  public filteredNotifications(notifications: ClientNotification[]): ClientNotification[] {
    return (notifications || []).filter((notification) => {
      if (this.activeFilter === "match") {
        return this.isMatchNotification(notification);
      }
      if (this.activeFilter === "manager") {
        return this.isManagerNotification(notification);
      }
      if (this.activeFilter === "account") {
        return this.isAccountNotification(notification);
      }
      return true;
    });
  }

  public isOlderThanWeek(notification: ClientNotification): boolean {
    if (!notification || !notification.createdAt) {
      return false;
    }

    const notificationTime = new Date(notification.createdAt).getTime();
    if (!Number.isFinite(notificationTime)) {
      return false;
    }

    return Date.now() - notificationTime > NotificationsComponent.OLD_NOTIFICATION_THRESHOLD_MS;
  }

  public notificationParams(notification: ClientNotification): {[key: string]: string | number} {
    const params = Object.assign({}, notification && notification.descriptionParams ? notification.descriptionParams : {});
    if (!params["match"] && notification && notification.event && notification.event.name) {
      params["match"] = notification.event.name;
    }
    if (!params["match"] && notification && notification.ratingReview && notification.ratingReview.event) {
      params["match"] = notification.ratingReview.event.name;
    }
    return params;
  }

  public notificationActionKey(notification: ClientNotification): string {
    if (!notification || !notification.actionKey) {
      return "notifications.openMatch";
    }
    return notification.actionKey === "events.openMatch" ? "notifications.openMatch" : notification.actionKey;
  }

  private isMatchNotification(notification: ClientNotification): boolean {
    return !!(notification && [
      "matchInvite",
      "matchResponseReminder",
      "matchTimeChanged",
      "matchLocationChanged",
      "matchCanceled",
      "matchFull",
      "rulebookReminder",
      "ratingReview"
    ].indexOf(notification.type) >= 0);
  }

  private isManagerNotification(notification: ClientNotification): boolean {
    return !!(notification && [
      "missingResult",
      "candidateRequest",
      "managerPlayerCancelled",
      "managerPlayerReturned",
      "managerJoinRequest",
      "lowPlayerCount",
      "pendingInvites",
      "sosRecommended",
      "publicRsvp"
    ].indexOf(notification.type) >= 0);
  }

  private isAccountNotification(notification: ClientNotification): boolean {
    return !!(notification && [
      "emailVerification",
      "profileLocationMissing",
      "noActiveMatches"
    ].indexOf(notification.type) >= 0);
  }

  public remindRatingReviewLater(notification: ClientNotification): void {
    if (!notification || !notification.ratingReview || this.actionLoadingId) {
      return;
    }

    this.actionLoadingId = notification.id;
    this.actionError = null;
    this.playerService.remindRatingReviewLater(notification.ratingReview.id).subscribe(
      () => {
        this.actionLoadingId = null;
        this.clientNotifications.refresh();
      },
      (error) => {
        this.actionError = this.getErrorMessage(error, this.translate.instant('notifications.ratingReviewReminderFailed'));
        this.handleAuthError(error);
        this.actionLoadingId = null;
      }
    );
  }

  private sendVerificationEmail(notification: ClientNotification): void {
    if (this.actionLoadingId) {
      return;
    }

    this.actionLoadingId = notification.id;
    this.playerService.sendEmailVerification().subscribe(
      () => {
        this.actionMessage = this.translate.instant('notifications.emailVerificationSent');
        this.actionLoadingId = null;
      },
      (error) => {
        if (this.isAlreadyVerifiedResponse(error)) {
          this.actionMessage = this.translate.instant('notifications.emailVerificationAlreadyConfirmedShort');
          this.actionLoadingId = null;
          this.clientNotifications.refresh();
          return;
        }

        this.actionError = this.getErrorMessage(error, this.translate.instant('notifications.emailVerificationSendFailed'));
        this.actionLoadingId = null;
      }
    );
  }

  private openMissingResultEvent(notification: ClientNotification): void {
    if (!notification.event) {
      return;
    }

    notification.event.historyEvent = true;
    localStorage.setItem("event", JSON.stringify(notification.event));
    localStorage.setItem("openResultDialogForEventId", notification.event.id);
    localStorage.removeItem("historyTargetEventId");
    this.router.navigate(["/event-players", false]);
  }

  private openActiveEvent(event: any): void {
    localStorage.setItem("event", JSON.stringify(event));
    this.router.navigate(["/event-players", false]);
  }

  private openRulebook(notification: ClientNotification): void {
    if (!notification.event) {
      return;
    }

    localStorage.setItem("event", JSON.stringify(notification.event));
    sessionStorage.setItem("openRulebookForEventId", notification.event.id);
    this.router.navigate(["/event-players", false]);
  }

  private openPlayerManagement(notification: ClientNotification): void {
    if (!notification.event) {
      return;
    }

    localStorage.setItem("event", JSON.stringify(notification.event));
    sessionStorage.setItem("matchHomeAction", JSON.stringify({eventId: notification.event.id, action: "players"}));
    this.router.navigate(["/event-players", false], {queryParams: {eventId: notification.event.id}});
  }

  private openRatingReview(notification: ClientNotification): void {
    if (!notification.ratingReview) {
      return;
    }

    this.analytics.track("rating_review_opened", {
      screen: "notifications",
      entityType: "rating_review",
      entityId: notification.ratingReview.id
    });
    this.router.navigate(["/rating-review", notification.ratingReview.id]);
  }

  private handleAuthError(error: any): void {
    if (error && (error.status === 401 || error.status === 403)) {
      this.authRedirect.login().subscribe();
    }
  }

  private isAlreadyVerifiedResponse(res: any): boolean {
    const backendMessage = this.getBackendMessage(res);
    return backendMessage.indexOf("ALREADY_VERIFIED") > -1 || backendMessage.indexOf("EMAIL_ALREADY_VERIFIED") > -1 || backendMessage.indexOf("already verified") > -1;
  }

  private getErrorMessage(response: any, fallback: string): string {
    const backendMessage = this.getBackendMessage(response);
    return backendMessage || fallback;
  }

  private getBackendMessage(res: any): string {
    if (!res || !res.error) {
      return "";
    }

    return (res.error.errorMsg || res.error.message || res.error.error || res.error || "").toString();
  }
}
